import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Zap, Loader2 } from "lucide-react";
import { login, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BG =
  "https://images.unsplash.com/photo-1613665813446-82a78c468a1d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVscyUyMGFlcmlhbCUyMHdoaXRlJTIwbW9kZXJufGVufDB8fHx8MTc4NjY0MjY2OXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      signIn(data.token, data.user);
      navigate("/devices");
    } catch (err) {
      let msg;
      if (!err.response) {
        msg = "Сервер недоступний. Перевірте, що бекенд працює (docker compose ps / logs backend).";
      } else if (err.response.status === 401) {
        msg = "Невірний email або пароль.";
      } else if (err.response.status === 429) {
        msg = "Забагато спроб входу. Зачекайте хвилину і спробуйте знову.";
      } else if (err.response.status === 403) {
        msg = formatApiError(err.response?.data?.detail) || "Доступ обмежено.";
      } else if (err.response.status >= 500) {
        msg = "Помилка сервера. Перевірте логи бекенда та зʼєднання з базою даних.";
      } else {
        msg = formatApiError(err.response?.data?.detail) || "Помилка входу";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${BG})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-100/60 to-slate-100" />

      <div
        className="relative z-10 w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm"
        data-testid="login-card"
      >
        <div className="mb-8 flex flex-col items-center text-center">
          <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
            <Zap size={26} className="text-white" fill="white" />
          </span>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            VIWAT Monitor
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Портал моніторингу накопичення енергії
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Email
            </label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="admin@example.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500">
              Пароль
            </label>
            <input
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Увійти
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-slate-500">
          Немає акаунта?{" "}
          <Link
            to="/register"
            data-testid="login-register-link"
            className="font-semibold text-blue-600 hover:text-blue-700"
          >
            Зареєструватися
          </Link>
        </p>
      </div>
    </div>
  );
}
